import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useBoolean } from '@fluentui/react-hooks';
import {
  IconButton,
  IIconProps,
  Label,
  MessageBarType,
  PrimaryButton,
  SearchBox,
  Separator,
  Spinner,
  Text
} from '@fluentui/react';
import { useTranslation } from 'react-i18next';

// models
import { RelationShipTypeViewModel } from '../../../models/Entities/Person/RelationshipType/RelationshipTypeModels';

// components
import { ConfirmationButtonComponent } from '../../../commons/component/ConfirmationButton/ConfirmationButtonComponent';
import { MessageComponent } from '../../../commons/component/MessageComponent';
import RelationshipTypeFormComponent from './RelationshipType.Form.Component';

// functions
import * as ComponentFunctions from './RelationshipTypes.List.Functions';

const EditIcon: IIconProps = { iconName: 'Edit' };
const AddIcon: IIconProps = { iconName: 'Add' };

const RelationshipTypesListComponent = (props: ComponentFunctions.Props) => {
  const [t] = useTranslation();

  const [isFormOpen, { setTrue: openForm, setFalse: dismissForm }] = useBoolean(false);
  const [selectedRelationshipTypeId, setSelectedRelationshipTypeId] = useState<number | undefined>(undefined);
  const [searchText, setSearchText] = useState<string>('');

  useEffect(() => {
    props.GetRelationshipTypes();
  }, []);

  const normalizedSearchText = searchText.trim().toLowerCase();

  const filtered = props.relationshipTypes.filter((item: RelationShipTypeViewModel) => {
    if (normalizedSearchText === '') return true;
    return [item.name, item.description]
      .filter((value) => !!value)
      .some((value) => (value as string).toLowerCase().includes(normalizedSearchText));
  });

  const handleOpenCreate = () => {
    setSelectedRelationshipTypeId(undefined);
    openForm();
  };

  const handleEdit = (relationshipTypeId: number) => {
    setSelectedRelationshipTypeId(relationshipTypeId);
    openForm();
  };

  const handleDismissForm = () => {
    setSelectedRelationshipTypeId(undefined);
    dismissForm();
  };

  return (
    <>
      <div className="StaffControls">
        <div className="StaffActionButtons">
          <PrimaryButton
            text={t('persons.relationshipTypes.list.add', { defaultValue: 'Agregar tipo' })}
            iconProps={AddIcon}
            onClick={handleOpenCreate}
            className="actionButton"
          />
        </div>
        <SearchBox
          placeholder={t('persons.relationshipTypes.list.searchPlaceholder', { defaultValue: 'Buscar tipo' })}
          underlined={false}
          onChange={(
            event?: React.ChangeEvent<HTMLInputElement> | undefined,
            newValue?: string | undefined
          ) => setSearchText(newValue || '')}
          onClear={() => setSearchText('')}
        />
      </div>
      <Separator />

      {props.failOnLoading && (
        <MessageComponent
          message={t('persons.relationshipTypes.list.error', {
            defaultValue: 'No fue posible obtener los tipos de relación'
          })}
          subMessage={props.error?.ErrorMessage || ''}
          type={MessageBarType.error}
          itemList={props.error?.Errors || []}
        />
      )}

      {!props.isLoading && filtered.length === 0 && (
        <MessageComponent
          message={t('persons.relationshipTypes.list.empty', {
            defaultValue: 'No hay tipos de relación registrados'
          })}
          type={MessageBarType.info}
        />
      )}

      {filtered.length > 0 && (
        <table className="Table">
          <thead>
            <tr>
              <th>{t('persons.relationshipTypes.list.name', { defaultValue: 'Nombre' })}</th>
              <th>{t('persons.relationshipTypes.list.description', { defaultValue: 'Descripción' })}</th>
              <th>{t('persons.relationshipTypes.list.appliesTo', { defaultValue: 'Aplica a' })}</th>
              <th>{t('persons.relationshipTypes.list.isActive', { defaultValue: 'Activo' })}</th>
              <th style={{ width: '140px' }}>
                {t('persons.common.actions', { defaultValue: 'Acciones' })}
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((item: RelationShipTypeViewModel) => (
              <tr key={item.id} className="TableBodyRow">
                <td className="primary">
                  <Label className="upper">{item.name || '-'}</Label>
                </td>
                <td>
                  <Text>{item.description || '-'}</Text>
                </td>
                <td>
                  <Text>
                    {[
                      item.isForNaturalPersons
                        ? t('persons.personType.natural', { defaultValue: 'Persona física' })
                        : undefined,
                      item.isForLegalPersons
                        ? t('persons.personType.juridical', { defaultValue: 'Persona jurídica' })
                        : undefined
                    ]
                      .filter((value) => !!value)
                      .join(' / ') || '-'}
                  </Text>
                </td>
                <td>
                  <Text>
                    {item.isActive
                      ? t('persons.common.yes', { defaultValue: 'Sí' })
                      : t('persons.common.no', { defaultValue: 'No' })}
                  </Text>
                </td>
                <td style={{ width: '140px' }}>
                  <IconButton
                    iconProps={EditIcon}
                    title={t('persons.common.edit', { defaultValue: 'Editar' })}
                    ariaLabel={t('persons.common.edit', { defaultValue: 'Editar' })}
                    onClick={() => handleEdit(item.id)}
                  />
                  <div className="danger">
                    <ConfirmationButtonComponent
                      ButtonId={`RelationshipTypeDeleteButton-${item.id}`}
                      ButtonIconName={'Delete'}
                      ButtonText={''}
                      ButtonCssClass={''}
                      ConfirmationButtonText={t('persons.common.delete', { defaultValue: 'Eliminar' })}
                      ConfirmationButtonCssClass={'deleteButton'}
                      CancelButtonText={t('persons.common.cancel', { defaultValue: 'Cancelar' })}
                      CancelButtonCssClass={''}
                      Title={t('persons.relationshipTypes.list.deleteTitle', {
                        defaultValue: 'Eliminar tipo de relación'
                      })}
                      Description={t('persons.relationshipTypes.list.deleteDescription', {
                        defaultValue: '¿Desea eliminar el tipo de relación seleccionado?'
                      })}
                      ElementTextHeader={t('persons.relationshipTypes.list.name', { defaultValue: 'Nombre' })}
                      ElementTextInformation={item.name || ''}
                      onConfirmation={() => props.DeleteRelationshipType(item.id)}
                      onCancel={() => undefined}
                      isOperationExecuting={props.isDeleting || false}
                      isOperationExecutedWithError={props.failOnDeleting || false}
                      OperationError={props.error}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {props.isLoading && (
        <div>
          <Spinner
            label={t('persons.relationshipTypes.list.loading', { defaultValue: 'Cargando tipos de relación...' })}
            ariaLive="assertive"
            labelPosition="top"
          />
        </div>
      )}

      <RelationshipTypeFormComponent
        isOpen={isFormOpen}
        openPanel={openForm}
        dismissPanel={handleDismissForm}
        relationshipTypeId={selectedRelationshipTypeId}
      />
    </>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(RelationshipTypesListComponent as any);
